import { useCallback, useRef, useState } from 'react';
import type { BulkProductEditFields } from './BulkEditProductsModal';

export type BulkUpdateProductsPayload = { ids: number[] } & BulkProductEditFields;

type UseProductBulkEditArgs = {
  onBulkUpdate: (payload: BulkUpdateProductsPayload) => Promise<void>;
};

// Architectural summary: Owns row selection + bulk-modal state and runs the guarded bulk submit.
export function useProductBulkEdit({ onBulkUpdate }: UseProductBulkEditArgs) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);

  const [isSelectionMode, setIsSelectionMode] = useState(false);

  const isSelected = useCallback((id: number) => selectedIds.has(id), [selectedIds]);

  const toggle = useCallback((id: number) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      if (next.size === 0) {
        setIsSelectionMode(false);
      }
      return next;
    });
  }, []);

  const setAll = useCallback((ids: number[], checked: boolean) => {
    setSelectedIds(checked ? new Set(ids) : new Set());
    if (!checked) {
      setIsSelectionMode(false);
    }
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setIsSelectionMode(false);
  }, []);

  const startSelectionMode = useCallback((id: number) => {
    setIsSelectionMode(true);
    setSelectedIds((previous) => new Set(previous).add(id));
  }, []);

  const openModal = useCallback(() => setIsModalOpen(true), []);

  const closeModal = useCallback(() => {
    if (!submitInFlightRef.current) {
      setIsModalOpen(false);
    }
  }, []);

  const submit = useCallback(
    async (fields: BulkProductEditFields) => {
      if (submitInFlightRef.current || selectedIds.size === 0) {
        return;
      }
      submitInFlightRef.current = true;
      setIsSubmitting(true);
      try {
        await onBulkUpdate({ ids: Array.from(selectedIds), ...fields });
        setIsModalOpen(false);
        setSelectedIds(new Set());
        setIsSelectionMode(false);
      } finally {
        submitInFlightRef.current = false;
        setIsSubmitting(false);
      }
    },
    [onBulkUpdate, selectedIds],
  );

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    isModalOpen,
    isSubmitting,
    isSelectionMode,
    isSelected,
    toggle,
    setAll,
    clearSelection,
    startSelectionMode,
    openModal,
    closeModal,
    submit,
  };
}
